/**
 * Enums do domínio.
 *
 * Estes valores são a fonte de verdade compartilhada entre API, worker e os três
 * frontends. O schema.prisma declara enums Postgres com EXATAMENTE os mesmos
 * membros, e o teste packages/database/test/enum-parity.test.ts falha se as duas
 * listas divergirem — assim ninguém adiciona um status no banco e esquece do
 * frontend (ou vice-versa).
 *
 * Definimos como objeto `as const` em vez de `enum` do TypeScript porque:
 *   1. `enum` gera código em runtime e não é apagável sob isolatedModules;
 *   2. os frontends não podem importar o client do Prisma (traria o engine junto).
 */

export const UserRole = {
  PLATFORM_ADMIN: 'PLATFORM_ADMIN',
  TENANT_ADMIN: 'TENANT_ADMIN',
  BARBER: 'BARBER',
  CUSTOMER: 'CUSTOMER',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

/** Papéis que operam dentro de uma barbearia (têm tenantId obrigatório). */
export const TENANT_SCOPED_ROLES: readonly UserRole[] = [
  UserRole.TENANT_ADMIN,
  UserRole.BARBER,
  UserRole.CUSTOMER,
];

export const TenantStatus = {
  /** Período de avaliação: acesso total, ainda sem assinatura SaaS paga. */
  TRIALING: 'TRIALING',
  ACTIVE: 'ACTIVE',
  /** Pagamento da assinatura SaaS falhou; dentro do grace period, segue operando. */
  PAST_DUE: 'PAST_DUE',
  /** Grace period esgotado: somente leitura + tela de regularização. Dados preservados. */
  SUSPENDED: 'SUSPENDED',
  CANCELLED: 'CANCELLED',
} as const;
export type TenantStatus = (typeof TenantStatus)[keyof typeof TenantStatus];

/** Estados em que a barbearia pode criar/alterar dados. */
export const TENANT_WRITABLE_STATUSES: readonly TenantStatus[] = [
  TenantStatus.TRIALING,
  TenantStatus.ACTIVE,
  TenantStatus.PAST_DUE,
];

export const EntityStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;
export type EntityStatus = (typeof EntityStatus)[keyof typeof EntityStatus];

export const AppointmentStatus = {
  PENDING: 'PENDING',
  CONFIRMED: 'CONFIRMED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  NO_SHOW: 'NO_SHOW',
} as const;
export type AppointmentStatus = (typeof AppointmentStatus)[keyof typeof AppointmentStatus];

/**
 * Status que ocupam o horário do barbeiro.
 *
 * Espelha exatamente o predicado da constraint EXCLUDE appointments_no_overlap
 * no Postgres. Mudar esta lista exige uma migration correspondente.
 */
export const BLOCKING_APPOINTMENT_STATUSES: readonly AppointmentStatus[] = [
  AppointmentStatus.PENDING,
  AppointmentStatus.CONFIRMED,
];

/** Status terminais: não admitem nova transição. */
export const TERMINAL_APPOINTMENT_STATUSES: readonly AppointmentStatus[] = [
  AppointmentStatus.COMPLETED,
  AppointmentStatus.CANCELLED,
  AppointmentStatus.NO_SHOW,
];

/** Transições permitidas na máquina de estados do agendamento. */
export const APPOINTMENT_TRANSITIONS: Readonly<
  Record<AppointmentStatus, readonly AppointmentStatus[]>
> = {
  PENDING: [
    AppointmentStatus.CONFIRMED,
    AppointmentStatus.CANCELLED,
    AppointmentStatus.NO_SHOW,
    AppointmentStatus.COMPLETED,
  ],
  CONFIRMED: [AppointmentStatus.COMPLETED, AppointmentStatus.CANCELLED, AppointmentStatus.NO_SHOW],
  COMPLETED: [],
  CANCELLED: [],
  NO_SHOW: [],
};

export const AppointmentSource = {
  CUSTOMER_APP: 'CUSTOMER_APP',
  ADMIN_PANEL: 'ADMIN_PANEL',
  BARBER_APP: 'BARBER_APP',
  WHATSAPP: 'WHATSAPP',
} as const;
export type AppointmentSource = (typeof AppointmentSource)[keyof typeof AppointmentSource];

export const RecurrenceStatus = {
  /** Barbearia exige confirmação: a vaga já está segurada, falta o balcão aprovar. */
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  CANCELLED: 'CANCELLED',
} as const;
export type RecurrenceStatus = (typeof RecurrenceStatus)[keyof typeof RecurrenceStatus];

/** Estados em que o horário fixo segura a vaga e gera as próximas semanas. */
export const ACTIVE_RECURRENCE_STATUSES: readonly RecurrenceStatus[] = [
  RecurrenceStatus.PENDING,
  RecurrenceStatus.ACTIVE,
];

export const RecurrenceCancelReason = {
  CUSTOMER: 'CUSTOMER',
  BARBERSHOP: 'BARBERSHOP',
  /** Duas faltas seguidas. */
  NO_SHOWS: 'NO_SHOWS',
  /** Três semanas seguidas sem vaga. */
  SLOT_UNAVAILABLE: 'SLOT_UNAVAILABLE',
  BARBER_OR_SERVICE_UNAVAILABLE: 'BARBER_OR_SERVICE_UNAVAILABLE',
} as const;
export type RecurrenceCancelReason =
  (typeof RecurrenceCancelReason)[keyof typeof RecurrenceCancelReason];

export const OccurrenceOutcome = {
  GENERATED: 'GENERATED',
  SKIPPED: 'SKIPPED',
} as const;
export type OccurrenceOutcome = (typeof OccurrenceOutcome)[keyof typeof OccurrenceOutcome];

export const SubscriptionStatus = {
  /** Criada, aguardando o pagador autorizar no Mercado Pago. */
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  PAST_DUE: 'PAST_DUE',
  PAUSED: 'PAUSED',
  CANCELLED: 'CANCELLED',
  EXPIRED: 'EXPIRED',
} as const;
export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

/**
 * Status em que a assinatura concede benefícios ao cliente.
 *
 * PAST_DUE continua concedendo durante o grace period: a régua de corte é o fim
 * do ciclo pago, não a primeira cobrança recusada.
 */
export const BENEFIT_GRANTING_STATUSES: readonly SubscriptionStatus[] = [
  SubscriptionStatus.ACTIVE,
  SubscriptionStatus.PAST_DUE,
];

export const PaymentStatus = {
  PENDING: 'PENDING',
  IN_PROCESS: 'IN_PROCESS',
  APPROVED: 'APPROVED',
  AUTHORIZED: 'AUTHORIZED',
  REJECTED: 'REJECTED',
  REFUNDED: 'REFUNDED',
  CHARGED_BACK: 'CHARGED_BACK',
  CANCELLED: 'CANCELLED',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export const BenefitType = {
  /** N usos de um serviço por ciclo. Ex.: 4 cortes. */
  SERVICE_QUOTA: 'SERVICE_QUOTA',
  /** Desconto percentual. Ex.: 10% em tudo. */
  PERCENT_DISCOUNT: 'PERCENT_DISCOUNT',
  /** Desconto de valor fixo, em centavos. */
  FIXED_DISCOUNT: 'FIXED_DISCOUNT',
} as const;
export type BenefitType = (typeof BenefitType)[keyof typeof BenefitType];

export const BenefitUsageStatus = {
  /** Reservado na criação do agendamento. Já conta contra a cota. */
  RESERVED: 'RESERVED',
  /** Confirmado quando o atendimento é concluído. */
  CONSUMED: 'CONSUMED',
  /** Devolvido à cota após cancelamento. */
  RELEASED: 'RELEASED',
} as const;
export type BenefitUsageStatus = (typeof BenefitUsageStatus)[keyof typeof BenefitUsageStatus];

/** Estados de uso que consomem cota (reservado ainda segura a vaga). */
export const QUOTA_CONSUMING_USAGE_STATUSES: readonly BenefitUsageStatus[] = [
  BenefitUsageStatus.RESERVED,
  BenefitUsageStatus.CONSUMED,
];

export const BlockType = {
  DAY_OFF: 'DAY_OFF',
  BREAK: 'BREAK',
  HOLIDAY: 'HOLIDAY',
  CUSTOM: 'CUSTOM',
} as const;
export type BlockType = (typeof BlockType)[keyof typeof BlockType];

export const OtpChannel = {
  WHATSAPP: 'WHATSAPP',
  SMS: 'SMS',
} as const;
export type OtpChannel = (typeof OtpChannel)[keyof typeof OtpChannel];

export const OtpPurpose = {
  LOGIN: 'LOGIN',
  PHONE_CHANGE: 'PHONE_CHANGE',
  /** Cadastrar ou trocar a chave de saque, e cada saque. */
  PAYOUT: 'PAYOUT',
} as const;
export type OtpPurpose = (typeof OtpPurpose)[keyof typeof OtpPurpose];

export const MessageChannel = {
  WHATSAPP: 'WHATSAPP',
  SMS: 'SMS',
} as const;
export type MessageChannel = (typeof MessageChannel)[keyof typeof MessageChannel];

export const MessageStatus = {
  QUEUED: 'QUEUED',
  SCHEDULED: 'SCHEDULED',
  SENDING: 'SENDING',
  SENT: 'SENT',
  DELIVERED: 'DELIVERED',
  READ: 'READ',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
} as const;
export type MessageStatus = (typeof MessageStatus)[keyof typeof MessageStatus];

export const WebhookStatus = {
  RECEIVED: 'RECEIVED',
  PROCESSING: 'PROCESSING',
  PROCESSED: 'PROCESSED',
  FAILED: 'FAILED',
  /** Evento válido porém irrelevante para o domínio (ex.: topic desconhecido). */
  IGNORED: 'IGNORED',
} as const;
export type WebhookStatus = (typeof WebhookStatus)[keyof typeof WebhookStatus];

export const WebhookProvider = {
  MERCADO_PAGO: 'MERCADO_PAGO',
  WHATSAPP: 'WHATSAPP',
  REDFOX: 'REDFOX',
} as const;
export type WebhookProvider = (typeof WebhookProvider)[keyof typeof WebhookProvider];

// ---------------------------------------------------------------------------
// PIX e banco interno (ver docs/PIX.md)
// ---------------------------------------------------------------------------

export const PixKeyType = {
  CPF: 'CPF',
  CNPJ: 'CNPJ',
  EMAIL: 'EMAIL',
  PHONE: 'PHONE',
  RANDOM: 'RANDOM',
} as const;
export type PixKeyType = (typeof PixKeyType)[keyof typeof PixKeyType];

export const SubscriptionPaymentMethod = {
  MERCADO_PAGO: 'MERCADO_PAGO',
  PIX: 'PIX',
} as const;
export type SubscriptionPaymentMethod =
  (typeof SubscriptionPaymentMethod)[keyof typeof SubscriptionPaymentMethod];

export const PixChargeStatus = {
  PENDING: 'PENDING',
  PAID: 'PAID',
  /** O prazo do desconto passou sem pagamento. */
  EXPIRED: 'EXPIRED',
  /** Pago fora do prazo: virou crédito do cliente. */
  LATE_PAID: 'LATE_PAID',
  CANCELLED: 'CANCELLED',
} as const;
export type PixChargeStatus = (typeof PixChargeStatus)[keyof typeof PixChargeStatus];

export const PixChargeTarget = {
  APPOINTMENT: 'APPOINTMENT',
  SUBSCRIPTION: 'SUBSCRIPTION',
} as const;
export type PixChargeTarget = (typeof PixChargeTarget)[keyof typeof PixChargeTarget];

export const BankEntryKind = {
  PIX_IN: 'PIX_IN',
  LATE_PIX_IN: 'LATE_PIX_IN',
  PLAN_PIX_IN: 'PLAN_PIX_IN',
  CREDIT_USE: 'CREDIT_USE',
  RELEASE: 'RELEASE',
  TO_CREDIT: 'TO_CREDIT',
  PAYOUT: 'PAYOUT',
  PAYOUT_REVERSAL: 'PAYOUT_REVERSAL',
  MANUAL_ADJUST: 'MANUAL_ADJUST',
} as const;
export type BankEntryKind = (typeof BankEntryKind)[keyof typeof BankEntryKind];

export const PayoutStatus = {
  PENDING: 'PENDING',
  PAID: 'PAID',
  FAILED: 'FAILED',
  REVERSED: 'REVERSED',
} as const;
export type PayoutStatus = (typeof PayoutStatus)[keyof typeof PayoutStatus];

export const FeeMethod = {
  PIX_IN: 'PIX_IN',
  PIX_OUT: 'PIX_OUT',
} as const;
export type FeeMethod = (typeof FeeMethod)[keyof typeof FeeMethod];

export const FeeKind = {
  /** Centavos por transação. */
  FIXED: 'FIXED',
  /** Pontos-base sobre o valor (99 = 0,99 %). */
  PERCENT: 'PERCENT',
} as const;
export type FeeKind = (typeof FeeKind)[keyof typeof FeeKind];

/** Chaves de template de notificação reconhecidas pelo sistema. */
export const NotificationTemplateKey = {
  OTP_CODE: 'otp_code',
  APPOINTMENT_CONFIRMED: 'appointment_confirmed',
  APPOINTMENT_REMINDER_24H: 'appointment_reminder_24h',
  APPOINTMENT_REMINDER_2H: 'appointment_reminder_2h',
  APPOINTMENT_CANCELLED: 'appointment_cancelled',
  APPOINTMENT_RESCHEDULED: 'appointment_rescheduled',
  POST_APPOINTMENT: 'post_appointment',
  PLAN_ACTIVATED: 'plan_activated',
  PAYMENT_APPROVED: 'payment_approved',
  PAYMENT_PENDING: 'payment_pending',
  PAYMENT_FAILED: 'payment_failed',
  SUBSCRIPTION_DUE: 'subscription_due',
  /** O PIX da mensalidade do plano está disponível (plano pago por PIX). */
  PLAN_PIX_DUE: 'plan_pix_due',
  /** Código para confirmar a chave de saque ou um saque. */
  PAYOUT_CODE: 'payout_code',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  RECURRENCE_CREATED: 'recurrence_created',
  RECURRENCE_WEEK_SKIPPED: 'recurrence_week_skipped',
  RECURRENCE_CANCELLED: 'recurrence_cancelled',
} as const;
export type NotificationTemplateKey =
  (typeof NotificationTemplateKey)[keyof typeof NotificationTemplateKey];
